import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP, tp, p as pStatic } from '../../i18n/useP';
import { listBsLines, setBsCfCategory } from '../../api/financials_bs';
import '../CashFlowPage.css';

/* Catégories de flux de trésorerie — classement des lignes du BILAN.

   C'est du PARAMÉTRAGE de structure : déplacé de l'onglet Flux de trésorerie
   (un rapport) vers Paramètres le 2026-08-09, à côté des correspondances de
   comptes. Une ligne non classée est EXCLUE des flux : le tableau réconcilie
   alors par omission, sans le dire — d'où le compteur en tête.

   Ordre = lecture du tableau. Libellés résolus au chargement du module (tp),
   comme le reste de la table de flux. */
const CF_CATS = [
  { code: 'cash',             label: tp('phrases:p_e0e75da7e7'),             group: tp('phrases:p_cbf70ab998') },
  { code: 'wcr_asset',        label: tp('phrases:p_f247b582ba'),           group: tp('phrases:p_f4036a90f7') },
  { code: 'wcr_liability',    label: tp('phrases:p_c5f4782835'),          group: tp('phrases:p_f4036a90f7') },
  { code: 'immo_operational', label: tp('phrases:p_cf8bb4a4d9'), group: tp('phrases:p_ba3f6f4e01') },
  { code: 'immo_financial',   label: tp('phrases:p_aa995b92ef'),      group: tp('phrases:p_ba3f6f4e01') },
  { code: 'fin_debt',         label: tp('phrases:p_3f812841bd'),     group: tp('phrases:p_e1282d3276') },
  { code: 'equity',           label: tp('phrases:p_413aa5056a'), group: tp('phrases:p_e1282d3276') },
  { code: 'noncash_charge',   label: pStatic('Charges non décaissées (provisions)'), group: pStatic('Capacité d’autofinancement') },
  { code: 'equity_noncash',   label: tp('phrases:p_1e8845ab58'), group: tp('phrases:p_6f88fb8e78') },
  { code: 'pl_result',        label: tp('phrases:p_ad48ed2b4d'),   group: tp('phrases:p_6f88fb8e78') },
];

export default function CfCategoriesPage({ activeTenant, addToast }) {
  const p = useP();
  const [lines, setLines] = useState([]);
  const tenantId = activeTenant?.id;

  const reload = useCallback(() => {
    if (!tenantId) { setLines([]); return; }
    listBsLines(tenantId).then((r) => setLines(r.data || [])).catch(() => setLines([]));
  }, [tenantId]);
  useEffect(() => { reload(); }, [reload]);

  async function handleSetCat(lineId, cat) {
    const res = await setBsCfCategory(tenantId, lineId, cat || null);
    if (res.error) { addToast && addToast(res.error, 'error'); return; }
    reload();
  }

  if (!activeTenant) return null;
  const unclassified = lines.filter((l) => !l.cf_category && !l.is_pl_feed).length;

  return (
    <>
      {unclassified > 0 && (
        <p className="cf-unclassified-warn" style={{ cursor: 'default' }}>
          <strong>{unclassified} {unclassified > 1 ? p('lignes non classées') : p('ligne non classée')}</strong>
          {' — '}{p('leurs mouvements n’apparaissent dans aucun flux.')}
        </p>
      )}
      <CfClassifier lines={lines} onSetCat={handleSetCat} />
    </>
  );
}

function CfClassifier({ lines, onSetCat }) {
  const { t } = useTranslation();
  const p = useP();
  const [dragId, setDragId] = useState(null);
  const [overCat, setOverCat] = useState(null);

  const pool = lines.filter(l => !l.cf_category && !l.is_pl_feed);
  const byCat = {};
  CF_CATS.forEach(c => { byCat[c.code] = []; });
  lines.forEach(l => {
    const c = l.is_pl_feed ? 'pl_result' : l.cf_category;
    if (c && byCat[c]) byCat[c].push(l);
  });

  function drop(cat) {
    if (dragId) onSetCat(dragId, cat);
    setDragId(null); setOverCat(null);
  }
  const dropProps = cat => ({
    onDragOver: e => { if (dragId) { e.preventDefault(); setOverCat(cat); } },
    onDragLeave: () => setOverCat(c => (c === cat ? null : c)),
    onDrop: e => { e.preventDefault(); drop(cat); },
  });
  const chip = (l, locked) => (
    <span
      key={l.id}
      className={`cfc-chip${locked ? ' cfc-chip--locked' : ''}${dragId === l.id ? ' cfc-chip--dragging' : ''}`}
      draggable={!locked}
      onDragStart={locked ? undefined : e => { setDragId(l.id); e.dataTransfer.effectAllowed = 'move'; }}
      onDragEnd={() => { setDragId(null); setOverCat(null); }}
    >
      <span className="cfc-chip-grip">⠿</span>{l.label}
    </span>
  );

  // Regroupement par bandeau (Opérationnel / Investissement / Financement…).
  const groups = [];
  CF_CATS.forEach(c => {
    let g = groups.find(x => x.group === c.group);
    if (!g) { g = { group: c.group, cats: [] }; groups.push(g); }
    g.cats.push(c);
  });

  return (
    <div className="cf-card cfc">
      <div className="cf-card-head">
        <span className="cf-card-title">{p('Classer les lignes du bilan')}</span>
        <span className="cf-card-meta">{p('Glisse chaque ligne dans sa catégorie. « À classer » = non prises dans les flux.')}</span>
      </div>
      <div className="cfc-body">
        {groups.map(g => (
          <div key={g.group} className="cfc-group">
            <div className="cfc-group-h">{g.group}</div>
            <div className="cfc-buckets">
              {g.cats.map(c => (
                <div
                  key={c.code}
                  className={`cfc-bucket${overCat === c.code ? ' cfc-over' : ''}`}
                  {...dropProps(c.code)}
                >
                  <div className="cfc-bucket-h">{c.label}</div>
                  <div className="cfc-bucket-body">
                    {byCat[c.code].length === 0
                      ? <span className="cfc-empty">—</span>
                      : byCat[c.code].map(l => chip(l, c.code === 'pl_result' && l.is_pl_feed))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div
          className={`cfc-pool${overCat === '__pool__' ? ' cfc-over' : ''}`}
          {...dropProps(null)}
          onDragOver={e => { if (dragId) { e.preventDefault(); setOverCat('__pool__'); } }}
        >
          <div className="cfc-pool-h">{t('phrases:p_7461a96aa7', { count: pool.length })}</div>
          <div className="cfc-bucket-body">
            {pool.length === 0
              ? <span className="cfc-empty">{p('Tout est classé ✓')}</span>
              : pool.map(l => chip(l, false))}
          </div>
        </div>
      </div>
    </div>
  );
}
